import React from "react";
import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";

type Props = {
    password: string | null | undefined;
    eventId: number | string | undefined;
    title?: string;
    description?: string;
    // Optional block rendered between the description and the password form,
    // e.g. instructions on where to obtain the password.
    instructions?: React.ReactNode;
    children: React.ReactNode;
};

const STORAGE_PREFIX = "eventListPasswordUnlocked:";

const EventListPasswordGate = ({
    password,
    eventId,
    title = "Password Required",
    description = "This event is password protected. Enter the password to continue.",
    instructions,
    children,
}: Props) => {
    const [unlocked, setUnlocked] = React.useState(false);
    const [checked, setChecked] = React.useState(false);
    const [entered, setEntered] = React.useState("");
    const [error, setError] = React.useState(false);

    React.useEffect(() => {
        if (!password) {
            setUnlocked(true);
            setChecked(true);
            return;
        }
        if (eventId === undefined) {
            setChecked(true);
            return;
        }
        try {
            const stored = window.sessionStorage.getItem(
                STORAGE_PREFIX + eventId,
            );
            if (stored === password) {
                setUnlocked(true);
            }
        } catch {
            // sessionStorage may throw in some sandboxed contexts; fall through
        }
        setChecked(true);
    }, [password, eventId]);

    if (!password || unlocked) {
        return <>{children}</>;
    }

    // During SSR and the first client render, avoid flashing the password
    // screen before we know whether sessionStorage already has an unlock.
    if (!checked) {
        return null;
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (entered === password) {
            try {
                window.sessionStorage.setItem(
                    STORAGE_PREFIX + eventId,
                    entered,
                );
            } catch {
                // ignore storage errors; still unlock for this page view
            }
            setUnlocked(true);
            setError(false);
        } else {
            setError(true);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
            <Paper sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
                <Box sx={{ mb: 2 }}>
                    <LockIcon sx={{ fontSize: 48, color: "primary.main" }} />
                </Box>
                <Typography variant="h5" gutterBottom>
                    {title}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    {description}
                </Typography>
                {instructions && <Box sx={{ mb: 3 }}>{instructions}</Box>}
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: 2,
                    }}
                >
                    <TextField
                        fullWidth
                        type="password"
                        label="Password"
                        value={entered}
                        onChange={(e) => {
                            setEntered(e.target.value);
                            if (error) setError(false);
                        }}
                        error={error}
                        helperText={error ? "Incorrect password" : ""}
                        autoFocus
                        autoComplete="off"
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={!entered}
                    >
                        Unlock
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default EventListPasswordGate;
